import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, User, MessageCircle, Upload, Sparkles, Database } from 'lucide-react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { uploadImage } from '../services/imageUploadService';
import { syncGeniusLociToPrompt, syncAllGeniusLociToPrompts } from '../services/geniusLociSyncService';
import { generateAvatarWithImagen } from '../services/imagenService';
import JungArchetypeWheel from '../components/JungArchetypeWheel';
import VoiceSelector, { VoiceSettings } from '../components/VoiceSelector';
import GlobalRagKnowledge from './GlobalRagKnowledge';

const isDev = Boolean((import.meta as any).env?.DEV);
const logDebug = (...args: any[]) => {
  if (isDev) console.log(...args);
};

interface GeniusLoci {
  id: string;
  name: string;
  avatar: string;
  voice: string;
  voiceId?: string;
  voiceSettings?: VoiceSettings;
  specialties: string[];
  bio: string;
  quote: string;
  personality: string;
}

interface Prompt {
  id: string;
  geniusLociId: string;
  initialMessage: string;
  context: string;
  voice: string;
  personality: string;
  specialties: string[];
  bio: string;
  quote: string;
  avatar?: string;
  disableGlobalConcise?: boolean;
  guidelines: {
    tone: string;
    language: string;
    role: string;
    interaction_style: string;
    user_engagement: string;
  };
  knowledgeItems?: PromptKnowledgeItem[];
}

type PromptKnowledgeCategory =
  | 'storia'
  | 'curiosita'
  | 'geografia'
  | 'aneddoto'
  | 'frase'
  | 'documento';

interface PromptKnowledgeItem {
  id: string;
  title: string;
  category: PromptKnowledgeCategory;
  content: string;
  tags: string[];
  sourceType: 'manual' | 'file';
  sourceName?: string;
  createdAt: string;
}

interface ChatSettings {
  conciseResponsesGlobalEnabled: boolean;
}

export default function PromptsGeniusLoci() {
  const [geniusLoci, setGeniusLoci] = useState<Record<string, GeniusLoci>>({});
  const [prompts, setPrompts] = useState<Record<string, Prompt>>({});
  const [selectedGeniusLoci, setSelectedGeniusLoci] = useState<GeniusLoci | null>(null);
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isImportingKnowledge, setIsImportingKnowledge] = useState(false);
  const [isSavingChatSettings, setIsSavingChatSettings] = useState(false);
  const [activeTab, setActiveTab] = useState<'genius-loci' | 'prompts' | 'shared-library'>('genius-loci');
  const [chatSettings, setChatSettings] = useState<ChatSettings>({
    conciseResponsesGlobalEnabled: false
  });
  const [knowledgeDraft, setKnowledgeDraft] = useState<{
    title: string;
    category: PromptKnowledgeCategory;
    tags: string;
    content: string;
  }>({
    title: '',
    category: 'storia',
    tags: '',
    content: ''
  });

  const [geniusLociFormData, setGeniusLociFormData] = useState({
    name: '',
    avatar: '',
    voice: '',
    voiceId: '',
    voiceSettings: {
      speed: 1.0,
      pitch: 0.0,
      volume: 0.0
    } as VoiceSettings,
    specialties: [] as string[],
    bio: '',
    quote: '',
    personality: ''
  });

  const [promptFormData, setPromptFormData] = useState({
    geniusLociId: '',
    initialMessage: '',
    context: '',
    voice: '',
    personality: '',
    specialties: [] as string[],
    bio: '',
    quote: '',
    avatar: '',
    disableGlobalConcise: false,
    guidelines: {
      tone: '',
      language: '',
      role: '',
      interaction_style: '',
      user_engagement: ''
    },
    knowledgeItems: [] as PromptKnowledgeItem[]
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      logDebug('🔄 Caricando dati...');
      
      // Carica genius loci
      logDebug('👥 Caricando genius loci...');
      const geniusLociCol = collection(db, 'geniusLoci');
      const geniusLociSnapshot = await getDocs(geniusLociCol);
      const geniusLociData: Record<string, GeniusLoci> = {};
      geniusLociSnapshot.docs.forEach(doc => {
        geniusLociData[doc.id] = {
          id: doc.id,
          ...doc.data()
        } as GeniusLoci;
      });
      logDebug(`👥 Trovati ${Object.keys(geniusLociData).length} genius loci`);
      setGeniusLoci(geniusLociData);

      // Carica prompt
      logDebug('💬 Caricando prompt...');
      const promptsCol = collection(db, 'prompts');
      const promptsSnapshot = await getDocs(promptsCol);
      const promptsData: Record<string, Prompt> = {};
      promptsSnapshot.docs.forEach(doc => {
        promptsData[doc.id] = {
          id: doc.id,
          ...doc.data()
        } as Prompt;
      });
      logDebug(`💬 Trovati ${Object.keys(promptsData).length} prompt`);
      setPrompts(promptsData);

      const chatSettingsRef = doc(db, 'appSettings', 'chat');
      const chatSettingsSnap = await getDoc(chatSettingsRef);
      setChatSettings({
        conciseResponsesGlobalEnabled: chatSettingsSnap.exists()
          ? chatSettingsSnap.data().conciseResponsesGlobalEnabled === true
          : false
      });
      
      logDebug('✅ Caricamento dati completato');
    } catch (error) {
      console.error('❌ Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectGeniusLoci = (geniusLoci: GeniusLoci) => {
    setSelectedGeniusLoci(geniusLoci);
    setGeniusLociFormData({
      name: geniusLoci.name,
      avatar: geniusLoci.avatar,
      voice: geniusLoci.voice,
      voiceId: geniusLoci.voiceId || '',
      voiceSettings: geniusLoci.voiceSettings || {
        speed: 1.0,
        pitch: 0.0,
        volume: 0.0
      },
      specialties: geniusLoci.specialties,
      bio: geniusLoci.bio,
      quote: geniusLoci.quote,
      personality: geniusLoci.personality
    });
  };

  const handleSelectPrompt = (prompt: Prompt) => {
    setSelectedPrompt(prompt);
    setPromptFormData({
      geniusLociId: prompt.geniusLociId,
      initialMessage: prompt.initialMessage,
      context: prompt.context,
      voice: prompt.voice,
      personality: prompt.personality,
      specialties: prompt.specialties,
      bio: prompt.bio,
      quote: prompt.quote,
      avatar: prompt.avatar || '',
      disableGlobalConcise: prompt.disableGlobalConcise === true,
      guidelines: prompt.guidelines,
      knowledgeItems: prompt.knowledgeItems || []
    });
  };

  const handleNewGeniusLoci = () => {
    setSelectedGeniusLoci(null);
    setGeniusLociFormData({
      name: '',
      avatar: '',
      voice: '',
      voiceId: '',
      voiceSettings: {
        speed: 1.0,
        pitch: 0.0,
        volume: 0.0
      },
      specialties: [],
      bio: '',
      quote: '',
      personality: ''
    });
  };

  const handleNewPrompt = () => {
    setSelectedPrompt(null);
    setPromptFormData({
      geniusLociId: '',
      initialMessage: '',
      context: '',
      voice: '',
      personality: '',
      specialties: [],
      bio: '',
      quote: '',
      avatar: '',
      disableGlobalConcise: false,
      guidelines: {
        tone: '',
        language: '',
        role: '',
        interaction_style: '',
        user_engagement: ''
      },
      knowledgeItems: []
    });
  };

  const handleCancelGeniusLoci = () => {
    if (selectedGeniusLoci) {
      setGeniusLociFormData({
        name: selectedGeniusLoci.name,
        avatar: selectedGeniusLoci.avatar,
        voice: selectedGeniusLoci.voice,
        voiceId: selectedGeniusLoci.voiceId || '',
        voiceSettings: selectedGeniusLoci.voiceSettings || {
          speed: 1.0,
          pitch: 0.0,
          volume: 0.0
        },
        specialties: selectedGeniusLoci.specialties,
        bio: selectedGeniusLoci.bio,
        quote: selectedGeniusLoci.quote,
        personality: selectedGeniusLoci.personality
      });
    } else {
      setGeniusLociFormData({
        name: '',
        avatar: '',
        voice: '',
        voiceId: '',
        voiceSettings: {
          speed: 1.0,
          pitch: 0.0,
          volume: 0.0
        },
        specialties: [],
        bio: '',
        quote: '',
        personality: ''
      });
    }
  };

  const handleCancelPrompt = () => {
    if (selectedPrompt) {
      setPromptFormData({
        geniusLociId: selectedPrompt.geniusLociId,
        initialMessage: selectedPrompt.initialMessage,
        context: selectedPrompt.context,
        voice: selectedPrompt.voice,
        personality: selectedPrompt.personality,
        specialties: selectedPrompt.specialties,
        bio: selectedPrompt.bio,
        quote: selectedPrompt.quote,
        avatar: selectedPrompt.avatar || '',
        disableGlobalConcise: selectedPrompt.disableGlobalConcise === true,
        guidelines: selectedPrompt.guidelines,
        knowledgeItems: selectedPrompt.knowledgeItems || []
      });
    } else {
      setPromptFormData({
        geniusLociId: '',
        initialMessage: '',
        context: '',
        voice: '',
        personality: '',
        specialties: [],
        bio: '',
        quote: '',
        avatar: '',
        disableGlobalConcise: false,
        guidelines: {
          tone: '',
          language: '',
          role: '',
          interaction_style: '',
          user_engagement: ''
        },
        knowledgeItems: []
      });
    }
  };

  const parseKnowledgeTags = (rawTags: string): string[] => {
    return rawTags
      .split(',')
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);
  };

  const makeKnowledgeItem = (
    title: string,
    category: PromptKnowledgeCategory,
    content: string,
    tags: string[],
    sourceType: 'manual' | 'file',
    sourceName?: string
  ): PromptKnowledgeItem => ({
    id: `k_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    title: title.trim(),
    category,
    content: content.trim(),
    tags,
    sourceType,
    sourceName,
    createdAt: new Date().toISOString()
  });

  const chunkText = (text: string, maxLen = 1200): string[] => {
    const cleaned = text.replace(/\r/g, '').trim();
    if (!cleaned) return [];

    const paragraphs = cleaned
      .split(/\n{2,}/)
      .map((p) => p.trim())
      .filter(Boolean);

    const chunks: string[] = [];
    let current = '';

    for (const paragraph of paragraphs) {
      if (!current) {
        current = paragraph;
        continue;
      }

      if ((current.length + 2 + paragraph.length) <= maxLen) {
        current += `\n\n${paragraph}`;
      } else {
        chunks.push(current);
        current = paragraph;
      }
    }

    if (current) chunks.push(current);

    // Fallback per testi senza paragrafi o righe molto lunghe
    if (chunks.length === 0 && cleaned.length > 0) {
      for (let i = 0; i < cleaned.length; i += maxLen) {
        chunks.push(cleaned.slice(i, i + maxLen));
      }
    }

    return chunks;
  };

  const readKnowledgeFileContent = async (file: File): Promise<string> => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (extension === 'docx') {
      const mammoth = await import('mammoth/mammoth.browser');
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      return (result.value || '').trim();
    }
    return (await file.text()).trim();
  };

  const handleAddKnowledgeItem = () => {
    const title = knowledgeDraft.title.trim();
    const content = knowledgeDraft.content.trim();
    if (!title || !content) {
      alert('Inserisci almeno titolo e contenuto.');
      return;
    }

    const newItem = makeKnowledgeItem(
      title,
      knowledgeDraft.category,
      content,
      parseKnowledgeTags(knowledgeDraft.tags),
      'manual'
    );

    setPromptFormData((prev) => ({
      ...prev,
      knowledgeItems: [...prev.knowledgeItems, newItem]
    }));

    setKnowledgeDraft((prev) => ({
      ...prev,
      title: '',
      content: '',
      tags: ''
    }));
  };

  const handleRemoveKnowledgeItem = (itemId: string) => {
    setPromptFormData((prev) => ({
      ...prev,
      knowledgeItems: prev.knowledgeItems.filter((item) => item.id !== itemId)
    }));
  };

  const handleImportKnowledgeFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsImportingKnowledge(true);
      const raw = await readKnowledgeFileContent(file);
      const content = raw.trim();

      if (!content) {
        alert('Il file selezionato è vuoto.');
        return;
      }

      const fileName = file.name.replace(/\.[^/.]+$/, '').trim() || 'Documento';
      const chunks = chunkText(content);
      const tags = parseKnowledgeTags(knowledgeDraft.tags);

      const importedItems = chunks.map((chunk, index) =>
        makeKnowledgeItem(
          chunks.length > 1 ? `${fileName} (${index + 1}/${chunks.length})` : fileName,
          knowledgeDraft.category,
          chunk,
          tags,
          'file',
          file.name
        )
      );

      setPromptFormData((prev) => ({
        ...prev,
        knowledgeItems: [...prev.knowledgeItems, ...importedItems]
      }));

      alert(`Documento importato: ${importedItems.length} blocco/i aggiunti alla base conoscenza.`);
    } catch (error) {
      console.error('Errore import file base conoscenza:', error);
      alert('Errore durante l\'import del file. Usa file .docx/.txt/.md/.csv/.json/.html.');
    } finally {
      setIsImportingKnowledge(false);
      event.target.value = '';
    }
  };


  const handleGeniusLociAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const imagePath = `geniusLoci/${selectedGeniusLoci?.id || 'new'}/avatar/${Date.now()}.${file.name.split('.').pop()}`;
      const avatarUrl = await uploadImage(file, imagePath);
      setGeniusLociFormData({ ...geniusLociFormData, avatar: avatarUrl });
      logDebug('Avatar caricato con successo:', avatarUrl);
    } catch (error) {
      console.error('Errore durante il caricamento dell\'avatar:', error);
      alert('Errore durante il caricamento dell\'avatar');
    } finally {
      setIsUploading(false);
    }
  };

  const handleGenerateAvatarWithAI = async () => {
    if (!geniusLociFormData.name || !geniusLociFormData.bio) {
      alert('Per generare un avatar con AI, devi prima inserire almeno il Nome e la Bio del Genius Loci');
      return;
    }

    if (!window.confirm('Vuoi generare un avatar con AI? Questo utilizzerà il nome e la bio per creare un\'immagine artistica ed evocativa.')) {
      return;
    }

    try {
      setIsUploading(true);
      logDebug('🎨 [IMAGEN] Generazione avatar per:', geniusLociFormData.name);

      const result = await generateAvatarWithImagen(
        geniusLociFormData.name,
        geniusLociFormData.bio,
        geniusLociFormData.personality
      );

      if (result.success && result.imageUrl) {
        // L'immagine è stata generata, ora la carichiamo su Firebase Storage
        logDebug('📤 [IMAGEN] Caricamento immagine su Firebase Storage...');
        
        try {
          // Converti data URL in Blob
          const response = await fetch(result.imageUrl);
          const blob = await response.blob();
          
          // Crea un file dal blob
          const file = new File([blob], `avatar-${geniusLociFormData.name}-${Date.now()}.png`, {
            type: 'image/png'
          });
          
          // Carica su Firebase Storage
          const imagePath = `geniusLoci/${Date.now()}-${file.name}`;
          const uploadedUrl = await uploadImage(file, imagePath);
          
          // Aggiorna il campo avatar con l'URL di Firebase Storage
          setGeniusLociFormData({
            ...geniusLociFormData,
            avatar: uploadedUrl
          });
          
          alert('🎨 Avatar generato e caricato con successo!');
          logDebug('✅ [IMAGEN] Avatar generato e caricato su Firebase Storage:', uploadedUrl);
          
        } catch (uploadError) {
          console.error('❌ [IMAGEN] Errore caricamento su Firebase Storage:', uploadError);
          
          // Fallback: usa l'immagine generata direttamente
          setGeniusLociFormData({
            ...geniusLociFormData,
            avatar: result.imageUrl
          });
          
          alert('🎨 Avatar generato! (Caricamento su Firebase fallito, usando immagine locale)');
        }
      } else {
        // Fallback: mostra il prompt migliorato
        const promptText = result.error || 'Errore nella generazione';
        alert(`🎨 ${promptText}\n\nCopia il prompt e usalo su:\n- DALL-E (OpenAI)\n- Midjourney\n- Stable Diffusion\n- Leonardo.ai\n\nPoi carica l'immagine generata usando "Carica Avatar".`);
        logDebug('📋 [IMAGEN] Fallback prompt:', promptText);
      }
    } catch (error) {
      console.error('❌ [IMAGEN] Errore generazione avatar:', error);
      alert('Errore durante la generazione dell\'avatar');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSyncGeniusLociToPrompt = async (geniusLociId: string) => {
    if (!window.confirm('Vuoi sincronizzare questo Genius Loci con il suo Prompt? Questo aggiornerà automaticamente la personalità e le informazioni del prompt.')) {
      return;
    }

    try {
      setIsLoading(true);
      const success = await syncGeniusLociToPrompt(geniusLociId);
      if (success) {
        alert('Genius Loci sincronizzato con successo! La personalità è stata aggiornata nel prompt.');
        await loadData(); // Ricarica i dati
      } else {
        alert('Errore durante la sincronizzazione. Controlla la console per i dettagli.');
      }
    } catch (error) {
      console.error('Errore durante la sincronizzazione:', error);
      alert('Errore durante la sincronizzazione');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSyncAllGeniusLoci = async () => {
    if (!window.confirm('Vuoi sincronizzare tutti i Genius Loci con i loro Prompt? Questo aggiornerà automaticamente tutte le personalità.')) {
      return;
    }

    try {
      setIsLoading(true);
      const result = await syncAllGeniusLociToPrompts();
      alert(`Sincronizzazione completata! ${result.success} successi, ${result.errors} errori.`);
      await loadData(); // Ricarica i dati
    } catch (error) {
      console.error('Errore durante la sincronizzazione globale:', error);
      alert('Errore durante la sincronizzazione globale');
    } finally {
      setIsLoading(false);
    }
  };


  const handleSaveGeniusLoci = async () => {
    try {
      let geniusLociId: string;
      
      if (selectedGeniusLoci) {
        await updateDoc(doc(db, 'geniusLoci', selectedGeniusLoci.id), geniusLociFormData);
        geniusLociId = selectedGeniusLoci.id;
      } else {
        const newDoc = await addDoc(collection(db, 'geniusLoci'), geniusLociFormData);
        geniusLociId = newDoc.id;
      }
      
      // Sincronizza automaticamente con il Prompt
      logDebug('🔄 [AUTO-SYNC] Sincronizzazione automatica dopo salvataggio...');
      await syncGeniusLociToPrompt(geniusLociId);
      
      await loadData();
      setSelectedGeniusLoci(null);
    } catch (error) {
      console.error('Error saving genius loci:', error);
    }
  };

  const handleToggleGlobalConcise = async (enabled: boolean) => {
    try {
      setIsSavingChatSettings(true);
      setChatSettings({ conciseResponsesGlobalEnabled: enabled });
      await setDoc(
        doc(db, 'appSettings', 'chat'),
        {
          conciseResponsesGlobalEnabled: enabled,
          updatedAt: new Date().toISOString()
        },
        { merge: true }
      );
      logDebug(`✅ [CHAT SETTINGS] conciseResponsesGlobalEnabled: ${enabled}`);
    } catch (error) {
      console.error('Errore salvataggio impostazioni globali chat:', error);
      alert('Errore durante il salvataggio del flag globale.');
      await loadData();
    } finally {
      setIsSavingChatSettings(false);
    }
  };

  const handleSavePrompt = async () => {
    try {
      const payload = {
        ...promptFormData,
        knowledgeItems: (promptFormData.knowledgeItems || []).map((item) => ({
          ...item,
          title: item.title.trim(),
          content: item.content.trim(),
          tags: (item.tags || []).map((tag) => tag.trim()).filter(Boolean)
        }))
      };

      if (selectedPrompt) {
        await updateDoc(doc(db, 'prompts', selectedPrompt.id), payload);
      } else {
        await addDoc(collection(db, 'prompts'), payload);
      }
      await loadData();
      setSelectedPrompt(null);
    } catch (error) {
      console.error('Error saving prompt:', error);
    }
  };

  const handleDeleteGeniusLoci = async (geniusLociId: string) => {
    if (window.confirm('Sei sicuro di voler eliminare questo genius loci?')) {
      try {
        await deleteDoc(doc(db, 'geniusLoci', geniusLociId));
        await loadData();
      } catch (error) {
        console.error('Error deleting genius loci:', error);
      }
    }
  };

  const handleDeletePrompt = async (promptId: string) => {
    if (window.confirm('Sei sicuro di voler eliminare questo prompt?')) {
      try {
        await deleteDoc(doc(db, 'prompts', promptId));
        await loadData();
      } catch (error) {
        console.error('Error deleting prompt:', error);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-primary text-xl">Caricamento dati...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-primary text-white p-6">
        <div className="container mx-auto max-w-7xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <User size={24} />
              <div>
                <h1 className="text-2xl font-bold">Gestione Prompt e Genius Loci</h1>
                <p className="text-white/80 text-sm mt-1">
                  Gestisci Genius Loci, prompt specifici e Biblioteca Condivisa del progetto
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-7xl px-4 py-8">

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex space-x-4">
            <button
              onClick={() => setActiveTab('genius-loci')}
              className={`px-6 py-3 rounded-lg transition-colors duration-200 flex items-center space-x-2 ${
                activeTab === 'genius-loci'
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <User size={16} />
              <span>Genius Loci ({Object.keys(geniusLoci).length})</span>
            </button>
            <button
              onClick={() => setActiveTab('prompts')}
              className={`px-6 py-3 rounded-lg transition-colors duration-200 flex items-center space-x-2 ${
                activeTab === 'prompts'
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <MessageCircle size={16} />
              <span>Prompt ({Object.keys(prompts).length})</span>
            </button>
            <button
              onClick={() => setActiveTab('shared-library')}
              className={`px-6 py-3 rounded-lg transition-colors duration-200 flex items-center space-x-2 ${
                activeTab === 'shared-library'
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Database size={16} />
              <span>Biblioteca Condivisa</span>
            </button>
          </div>
        </div>

        {activeTab === 'shared-library' ? (
          <GlobalRagKnowledge embedded />
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Lista */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800">
                {activeTab === 'genius-loci' ? 'Genius Loci' : 'Prompt'}
              </h2>
              <div className="flex space-x-2">
                {activeTab === 'genius-loci' && (
                  <>
                    <button
                      onClick={handleSyncAllGeniusLoci}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                      title="Sincronizza tutti i Genius Loci con i loro Prompt"
                    >
                      <MessageCircle size={16} />
                      <span>Sincronizza Tutti</span>
                    </button>
                  </>
                )}
                <button
                  onClick={activeTab === 'genius-loci' ? handleNewGeniusLoci : handleNewPrompt}
                  className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                >
                  <Plus size={16} />
                  <span>Nuovo</span>
                </button>
              </div>
            </div>

            {activeTab === 'prompts' && (
              <div className="mb-4 p-3 border border-gray-200 rounded-lg bg-gray-50">
                <label className="flex items-center justify-between gap-4 cursor-pointer">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Flag Globale: Risposte concise</p>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Se attivo, tutti i chatbot rispondono in modo breve. Puoi disattivarlo per singolo prompt.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={chatSettings.conciseResponsesGlobalEnabled}
                    onChange={(e) => handleToggleGlobalConcise(e.target.checked)}
                    disabled={isSavingChatSettings}
                    className="h-5 w-5 accent-primary"
                  />
                </label>
              </div>
            )}
            
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {activeTab === 'genius-loci' ? (
                Object.values(geniusLoci).map((geniusLoci) => (
                  <div
                    key={geniusLoci.id}
                    className={`p-4 rounded-lg cursor-pointer transition-colors duration-200 ${
                      selectedGeniusLoci?.id === geniusLoci.id
                        ? 'bg-primary/10 border-2 border-primary'
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                    onClick={() => handleSelectGeniusLoci(geniusLoci)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-800">{geniusLoci.name}</h3>
                        <p className="text-sm text-gray-800/80 mt-1">{geniusLoci.personality}</p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {geniusLoci.specialties.slice(0, 3).map((specialty, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full"
                            >
                              {specialty}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSyncGeniusLociToPrompt(geniusLoci.id);
                          }}
                          className="p-2 bg-green-500/20 hover:bg-green-500/30 text-green-600 rounded-lg transition-colors duration-200"
                          title="Sincronizza con Prompt"
                        >
                          <MessageCircle size={16} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectGeniusLoci(geniusLoci);
                          }}
                          className="p-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-200 rounded-lg transition-colors duration-200"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteGeniusLoci(geniusLoci.id);
                          }}
                          className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-200 rounded-lg transition-colors duration-200"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                Object.values(prompts).map((prompt) => (
                  <div
                    key={prompt.id}
                    className={`p-4 rounded-lg cursor-pointer transition-colors duration-200 ${
                      selectedPrompt?.id === prompt.id
                        ? 'bg-primary/10 border-2 border-primary'
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                    onClick={() => handleSelectPrompt(prompt)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-800">
                          {geniusLoci[prompt.geniusLociId]?.name || 'Genius Loci sconosciuto'}
                        </h3>
                        <p className="text-sm text-gray-800/80 mt-1 line-clamp-2">
                          {prompt.initialMessage}
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          Base conoscenza: {(prompt.knowledgeItems || []).length} contenuti
                        </p>
                        {prompt.disableGlobalConcise === true && (
                          <p className="text-xs text-orange-700 mt-1">
                            Override: risposte concise globali disattivate
                          </p>
                        )}
                        <div className="flex flex-wrap gap-1 mt-2">
                          {prompt.specialties.slice(0, 3).map((specialty, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full"
                            >
                              {specialty}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectPrompt(prompt);
                          }}
                          className="p-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-200 rounded-lg transition-colors duration-200"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePrompt(prompt.id);
                          }}
                          className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-200 rounded-lg transition-colors duration-200"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Form */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              {activeTab === 'genius-loci' 
                ? (selectedGeniusLoci ? 'Modifica Genius Loci' : 'Nuovo Genius Loci')
                : (selectedPrompt ? 'Modifica Prompt' : 'Nuovo Prompt')
              }
            </h2>
            
            {activeTab === 'genius-loci' ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Nome</label>
                  <input
                    type="text"
                    value={geniusLociFormData.name}
                    onChange={(e) => setGeniusLociFormData({ ...geniusLociFormData, name: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Nome del genius loci"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Avatar</label>
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <label className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg cursor-pointer transition-colors">
                        <Upload size={20} className="text-gray-800" />
                        <span className="text-gray-800 font-medium">
                          {isUploading ? 'Caricamento...' : 'Carica'}
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleGeniusLociAvatarUpload}
                          className="hidden"
                          disabled={isUploading}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={handleGenerateAvatarWithAI}
                        disabled={isUploading || !geniusLociFormData.name || !geniusLociFormData.bio}
                        className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 disabled:from-gray-300 disabled:to-gray-300 text-white font-medium rounded-lg transition-all duration-200 disabled:cursor-not-allowed"
                        title={!geniusLociFormData.name || !geniusLociFormData.bio ? 'Inserisci Nome e Bio prima di generare' : 'Genera avatar con AI'}
                      >
                        <Sparkles size={20} />
                        <span>{isUploading ? 'Generando...' : 'Genera Avatar'}</span>
                      </button>
                    </div>
                    <input
                      type="url"
                      value={geniusLociFormData.avatar}
                      onChange={(e) => setGeniusLociFormData({ ...geniusLociFormData, avatar: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Oppure inserisci URL dell'avatar"
                    />
                    <p className="text-xs text-gray-500">🎨 Il bottone "Genera Avatar" crea un'immagine artistica ed evocativa del genius loci</p>
                    {geniusLociFormData.avatar && (
                      <div className="mt-2">
                        <img 
                          src={geniusLociFormData.avatar} 
                          alt="Anteprima avatar" 
                          className="w-20 h-20 object-cover rounded-full border-2 border-gray-300"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Voce</label>
                  <input
                    type="text"
                    value={geniusLociFormData.voice}
                    onChange={(e) => setGeniusLociFormData({ ...geniusLociFormData, voice: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Descrizione della voce"
                  />
                  <p className="text-xs text-gray-500 mt-1">Descrizione testuale della voce (opzionale)</p>
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Selezione Voce Google</label>
                  <VoiceSelector
                    selectedVoiceId={geniusLociFormData.voiceId}
                    onVoiceSelect={(voiceId) => {
                      setGeniusLociFormData({ 
                        ...geniusLociFormData, 
                        voiceId: voiceId 
                      });
                    }}
                    onVoiceSettingsChange={(settings) => {
                      setGeniusLociFormData({ 
                        ...geniusLociFormData, 
                        voiceSettings: settings 
                      });
                    }}
                    className="mt-2"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-4">Personalità (Archetipo di Jung)</label>
                  <JungArchetypeWheel
                    selectedArchetype={geniusLociFormData.personality}
                    onArchetypeSelect={(archetype) => {
                      setGeniusLociFormData({ 
                        ...geniusLociFormData, 
                        personality: archetype 
                      });
                    }}
                  />
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Bio</label>
                  <textarea
                    value={geniusLociFormData.bio}
                    onChange={(e) => setGeniusLociFormData({ ...geniusLociFormData, bio: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Biografia del genius loci"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Citazione</label>
                  <input
                    type="text"
                    value={geniusLociFormData.quote}
                    onChange={(e) => setGeniusLociFormData({ ...geniusLociFormData, quote: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Citazione del genius loci"
                  />
                </div>

                <div className="flex space-x-4 pt-4">
                  <button
                    onClick={handleSaveGeniusLoci}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-gray-800 px-6 py-3 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                  >
                    <Save size={16} />
                    <span>Salva</span>
                  </button>
                  <button
                    onClick={handleCancelGeniusLoci}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-gray-800 px-6 py-3 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                  >
                    <X size={16} />
                    <span>Annulla</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Genius Loci</label>
                  <select
                    value={promptFormData.geniusLociId}
                    onChange={(e) => {
                      const selectedGeniusLociId = e.target.value;
                      const selectedGeniusLoci = geniusLoci[selectedGeniusLociId];
                      
                      if (selectedGeniusLoci) {
                        // Aggiorna automaticamente i dati dal Genius Loci selezionato
                        setPromptFormData({
                          ...promptFormData,
                          geniusLociId: selectedGeniusLociId,
                          // Sincronizza i dati del Genius Loci
                          voice: selectedGeniusLoci.voice,
                          personality: selectedGeniusLoci.personality,
                          specialties: selectedGeniusLoci.specialties || [],
                          bio: selectedGeniusLoci.bio,
                          quote: selectedGeniusLoci.quote,
                          avatar: selectedGeniusLoci.avatar || ''
                        });
                        logDebug(`✅ [PROMPT] Caricati dati da Genius Loci: ${selectedGeniusLoci.name}, Personality: ${selectedGeniusLoci.personality}`);
                      } else {
                        setPromptFormData({ ...promptFormData, geniusLociId: selectedGeniusLociId });
                      }
                    }}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">Seleziona un genius loci</option>
                    {Object.values(geniusLoci).map((gl) => (
                      <option key={gl.id} value={gl.id}>
                        {gl.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">📥 Selezionando un Genius Loci, i campi saranno aggiornati automaticamente</p>
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Messaggio Iniziale</label>
                  <textarea
                    value={promptFormData.initialMessage}
                    onChange={(e) => setPromptFormData({ ...promptFormData, initialMessage: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Messaggio iniziale del prompt"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Contesto</label>
                  <textarea
                    value={promptFormData.context}
                    onChange={(e) => setPromptFormData({ ...promptFormData, context: e.target.value })}
                    rows={2}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Contesto del prompt"
                  />
                </div>

                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <label className="flex items-center justify-between gap-4 cursor-pointer">
                    <div>
                      <p className="text-sm font-medium text-gray-800">Override singolo prompt</p>
                      <p className="text-xs text-gray-600 mt-0.5">
                        Disattiva la concisione globale solo per questo chatbot.
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={promptFormData.disableGlobalConcise}
                      onChange={(e) => setPromptFormData({ ...promptFormData, disableGlobalConcise: e.target.checked })}
                      className="h-5 w-5 accent-primary"
                    />
                  </label>
                </div>

                <div className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <label className="block text-gray-700 text-sm font-medium">
                      Contenuti del Prompt (solo questo agente)
                    </label>
                    <span className="text-xs text-gray-600">
                      {(promptFormData.knowledgeItems || []).length} contenuti
                    </span>
                  </div>
                  <p className="text-xs text-gray-600">
                    Questi contenuti vengono usati solo da questo Genius Loci. Per contenuti comuni a tutti usa il tab "Biblioteca Condivisa".
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={knowledgeDraft.title}
                      onChange={(e) => setKnowledgeDraft((prev) => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Titolo contenuto"
                    />
                    <select
                      value={knowledgeDraft.category}
                      onChange={(e) => setKnowledgeDraft((prev) => ({ ...prev, category: e.target.value as PromptKnowledgeCategory }))}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="storia">Storia</option>
                      <option value="curiosita">Curiosita</option>
                      <option value="geografia">Geografia</option>
                      <option value="aneddoto">Aneddoto</option>
                      <option value="frase">Frase</option>
                      <option value="documento">Documento</option>
                    </select>
                  </div>

                  <input
                    type="text"
                    value={knowledgeDraft.tags}
                    onChange={(e) => setKnowledgeDraft((prev) => ({ ...prev, tags: e.target.value }))}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Tag separati da virgola (es. medioevo, monastero, Acquavella)"
                  />

                  <textarea
                    value={knowledgeDraft.content}
                    onChange={(e) => setKnowledgeDraft((prev) => ({ ...prev, content: e.target.value }))}
                    rows={4}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Contenuto da aggiungere alla base conoscenza..."
                  />

                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={handleAddKnowledgeItem}
                      className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors duration-200"
                    >
                      Aggiungi contenuto
                    </button>
                    <label className={`px-4 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2 ${
                      isImportingKnowledge
                        ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                    }`}>
                      <Upload size={14} />
                      <span>{isImportingKnowledge ? 'Import in corso...' : 'Importa documento'}</span>
                      <input
                        type="file"
                        accept=".docx,.txt,.md,.csv,.json,.html"
                        onChange={handleImportKnowledgeFile}
                        className="hidden"
                        disabled={isImportingKnowledge}
                      />
                    </label>
                  </div>

                  {(promptFormData.knowledgeItems || []).length > 0 && (
                    <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                      {(promptFormData.knowledgeItems || []).map((item) => (
                        <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-800 truncate">
                                {item.title}
                              </p>
                              <p className="text-xs text-gray-600 mt-0.5">
                                {item.category} • {item.sourceType === 'file' ? `file: ${item.sourceName || '-'}` : 'inserimento manuale'}
                              </p>
                              {item.tags.length > 0 && (
                                <p className="text-xs text-gray-600 mt-1 truncate">
                                  tag: {item.tags.join(', ')}
                                </p>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveKnowledgeItem(item.id)}
                              className="text-red-600 hover:text-red-700 text-xs"
                            >
                              Rimuovi
                            </button>
                          </div>
                          <p className="text-xs text-gray-700 mt-2 line-clamp-3">
                            {item.content}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Voce</label>
                  <input
                    type="text"
                    value={promptFormData.voice}
                    onChange={(e) => setPromptFormData({ ...promptFormData, voice: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Descrizione della voce"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Personalità (Archetipo di Jung)</label>
                  <div className="w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-gray-800 font-medium">
                    {promptFormData.personality || 'Nessuna personalità sincronizzata'}
                  </div>
                  <p className="text-xs text-gray-500 mt-1 flex items-center space-x-1">
                    <span>🔄</span>
                    <span>Questo campo viene sincronizzato automaticamente dal Genius Loci associato</span>
                  </p>
                </div>

                <div className="flex space-x-4 pt-4">
                  <button
                    onClick={handleSavePrompt}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-gray-800 px-6 py-3 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                  >
                    <Save size={16} />
                    <span>Salva</span>
                  </button>
                  <button
                    onClick={handleCancelPrompt}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-gray-800 px-6 py-3 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                  >
                    <X size={16} />
                    <span>Annulla</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
